/** Flat-index grid helpers for the generator.
 *
 * The generator works on one level (z = 0) and addresses cells the way
 * `AdventureMapState` does, `y * width + x`, so an index can be handed straight
 * to `computeReachable` without a conversion. Everything here is integer.
 */

import type { AdventureMapState, TerrainKind } from "@game/core";
import { TERRAINS, isWaterCell, isPassable } from "@game/core";

export interface Grid {
  readonly width: number;
  readonly height: number;
}

export const idx = (g: Grid, x: number, y: number): number => y * g.width + x;
export const xOf = (g: Grid, i: number): number => i % g.width;
export const yOf = (g: Grid, i: number): number => Math.trunc(i / g.width);
export const inside = (g: Grid, x: number, y: number): boolean =>
  x >= 0 && y >= 0 && x < g.width && y < g.height;

/** Chebyshev distance; the adventure map's diagonals cost what straights cost. */
export const chebyshev = (g: Grid, a: number, b: number): number =>
  Math.max(Math.abs(xOf(g, a) - xOf(g, b)), Math.abs(yOf(g, a) - yOf(g, b)));

export const ORTHO_DX: readonly number[] = Object.freeze([0, 1, 0, -1]);
export const ORTHO_DY: readonly number[] = Object.freeze([-1, 0, 1, 0]);
export const DIAG_DX: readonly number[] = Object.freeze([0, 1, 1, 1, 0, -1, -1, -1]);
export const DIAG_DY: readonly number[] = Object.freeze([-1, -1, 0, 1, 1, 1, 0, -1]);

export function orthoNeighbours(g: Grid, i: number, out: number[]): number[] {
  out.length = 0;
  const x = xOf(g, i), y = yOf(g, i);
  for (let k = 0; k < 4; k++) {
    const nx = x + ORTHO_DX[k]!, ny = y + ORTHO_DY[k]!;
    if (inside(g, nx, ny)) out.push(idx(g, nx, ny));
  }
  return out;
}

export function allNeighbours(g: Grid, i: number, out: number[]): number[] {
  out.length = 0;
  const x = xOf(g, i), y = yOf(g, i);
  for (let k = 0; k < 8; k++) {
    const nx = x + DIAG_DX[k]!, ny = y + DIAG_DY[k]!;
    if (inside(g, nx, ny)) out.push(idx(g, nx, ny));
  }
  return out;
}

export const terrainOf = (m: AdventureMapState, i: number): TerrainKind =>
  TERRAINS[m.terrain[i]!]!;

/**
 * Can a foot hero stand here at all? This is the predicate the placement loop
 * uses, and it is deliberately the same one `cellEnterable` uses for
 * `mode: "land"`, so a cell the generator calls connected is a cell the kernel
 * calls connected.
 */
/** `TERRAINS` index -> 1 when that terrain is water. See `flood`. */
const WATER_TERRAIN = Uint8Array.from(TERRAINS.map((t) => (t === "water" ? 1 : 0)));
/** The move cost that means "no". Mirrors `@game/core`'s own constant. */
const IMPASSABLE = 0;

export const walkable = (m: AdventureMapState, i: number): boolean =>
  !isWaterCell(m, i) && isPassable(m, i);

/**
 * Flood fill over `walkable` cells, eight-connected because heroes move
 * diagonally. Returns the visited mask. Used as the inner-loop connectivity
 * gate; `computeReachable` is the authority and runs at the end of generation.
 */
export function flood(m: AdventureMapState, origin: number, out?: Uint8Array): Uint8Array {
  const w = m.width, h = m.height;
  const n = w * h;
  const seen = out ?? new Uint8Array(n);
  seen.fill(0);
  if (origin < 0 || origin >= n || !walkable(m, origin)) return seen;

  // Hand-rolled rather than `allNeighbours` + `walkable`, because this is the
  // generator's hottest loop by a wide margin: every blocking placement runs one
  // of these over the whole map, and a 252x252 map takes some fifteen hundred of
  // them. The helpers cost an array write and two function calls per neighbour;
  // read straight out of the typed arrays it is the same answer several times
  // faster, and it is the same answer - `walkable` is `!water && moveCost != 0`.
  const cost = m.moveCost;
  const terrain = m.terrain;
  const stack = new Int32Array(n);
  let top = 0;
  stack[top++] = origin;
  seen[origin] = 1;
  while (top > 0) {
    const cur = stack[--top]!;
    const x = cur % w, y = (cur - (cur % w)) / w;
    const x0 = x > 0 ? x - 1 : 0, x1 = x < w - 1 ? x + 1 : w - 1;
    const y0 = y > 0 ? y - 1 : 0, y1 = y < h - 1 ? y + 1 : h - 1;
    for (let ny = y0; ny <= y1; ny++) {
      const row = ny * w;
      for (let nx = x0; nx <= x1; nx++) {
        const nb = row + nx;
        if (seen[nb] === 1) continue;
        if (cost[nb] === IMPASSABLE) continue;
        if (WATER_TERRAIN[terrain[nb]!] === 1) continue;
        seen[nb] = 1;
        stack[top++] = nb;
      }
    }
  }
  return seen;
}

/**
 * Cheapest orthogonal path under a caller-supplied cost, or null.
 *
 * Orthogonal: this is what cuts water channels and reopens ridges, where the
 * result is painted TERRAIN three cells wide and a corner step would leave a
 * pinch. Roads and rivers are overlays with a frame for a corner join and are
 * routed by `cheapestPath8` below.
 */
export function cheapestPath(
  g: Grid, from: number, to: number, cost: (i: number) => number,
): number[] | null {
  const n = g.width * g.height;
  const dist = new Int32Array(n).fill(0x7fffffff);
  const prev = new Int32Array(n).fill(-1);
  const c0 = cost(from);
  if (c0 < 0 || cost(to) < 0) return null;
  dist[from] = 0;

  // Binary heap over (dist, cell), ties broken by cell index so the path is a
  // function of the inputs and not of push order.
  const hd: number[] = [0], hc: number[] = [from];
  const less = (i: number, j: number): boolean =>
    hd[i]! !== hd[j]! ? hd[i]! < hd[j]! : hc[i]! < hc[j]!;
  const swap = (i: number, j: number): void => {
    const d = hd[i]!; hd[i] = hd[j]!; hd[j] = d;
    const c = hc[i]!; hc[i] = hc[j]!; hc[j] = c;
  };
  const up = (i0: number): void => {
    let i = i0;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!less(i, p)) break;
      swap(i, p); i = p;
    }
  };
  const down = (i0: number): void => {
    let i = i0;
    for (;;) {
      const l = i * 2 + 1, r = l + 1;
      let s = i;
      if (l < hd.length && less(l, s)) s = l;
      if (r < hd.length && less(r, s)) s = r;
      if (s === i) break;
      swap(i, s); i = s;
    }
  };

  const buf: number[] = [];
  while (hd.length > 0) {
    const d = hd[0]!, cur = hc[0]!;
    const last = hd.length - 1;
    swap(0, last); hd.pop(); hc.pop();
    if (hd.length > 0) down(0);
    if (d > dist[cur]!) continue;
    if (cur === to) break;
    orthoNeighbours(g, cur, buf);
    for (const nb of buf) {
      const w = cost(nb);
      if (w < 0) continue;
      const nd = d + w;
      if (nd < dist[nb]!) {
        dist[nb] = nd;
        prev[nb] = cur;
        hd.push(nd); hc.push(nb); up(hd.length - 1);
      }
    }
  }

  if (dist[to] === 0x7fffffff) return null;
  const path: number[] = [];
  for (let cur = to; cur !== -1; cur = prev[cur]!) {
    path.push(cur);
    if (cur === from) break;
  }
  path.reverse();
  return path[0] === from ? path : null;
}

/**
 * How a step is priced on the eight-connected router, in tenths of a cell.
 *
 * A diagonal step covers root-two cells and costs `DIAG_STEP / STRAIGHT_STEP`
 * = 1.4 of a straight one, so per unit of ground it is no dearer - which is
 * exactly what makes the router take the diagonal instead of the staircase
 * that covers the same ground for 2.0. The turn table is what keeps a route
 * from zig-zagging: a 45-degree change of heading is nearly free, a right
 * angle costs a cell and a half, and doubling back is priced out. The cheapest
 * way from A to B on open ground is therefore one diagonal leg and one straight
 * leg joined by a single 45-degree bend, which is the shape of a classic road.
 */
export const STRAIGHT_STEP = 10;
export const DIAG_STEP = 14;
/** Indexed by the change of heading in 45-degree units, 0..4. */
export const TURN_COST: readonly number[] = Object.freeze([0, 3, 15, 40, 80]);

/**
 * The cheapest a cell can be under any of the routers' cost functions: an
 * existing road, which `roadCost` prices at 2 so a new route reuses it. The
 * A* heuristic below is built on it, so it must never exceed what a cost
 * function actually returns for a passable cell.
 */
export const MIN_CELL_COST = 2;

/**
 * Cheapest EIGHT-connected path under a caller-supplied per-cell cost, with a
 * heading-change penalty, or null.
 *
 * This is what lays roads and traces rivers now that the overlay atlas has a
 * frame for a corner join. Exact, not a heuristic: the search state is (cell,
 * heading), so the turn cost is part of what the search minimises rather than
 * a tie-break, and the route it returns is the cheapest under the whole price.
 * Eight headings per cell is eight times the states of the orthogonal search,
 * so the search is A* over octile distance at `MIN_CELL_COST` a cell - an
 * admissible, consistent heuristic, which keeps the answer exact and stops the
 * frontier flooding the whole map on a long route.
 *
 * A diagonal step is refused when BOTH cells flanking it are impassable
 * (`cost < 0`): a road that squeezes between two rocks corner to corner draws
 * its band across the rocks, and a hero could not follow it anyway.
 */
export function cheapestPath8(
  g: Grid, from: number, to: number, cost: (i: number) => number,
  turnCost: readonly number[] = TURN_COST,
): number[] | null {
  const w = g.width, h = g.height;
  const n = w * h;
  const states = n * 8;
  const dist = new Int32Array(states).fill(0x7fffffff);
  const prev = new Int32Array(states).fill(-1);
  if (cost(from) < 0 || cost(to) < 0) return null;
  const tx = to % w, ty = (to - tx) / w;

  // per-cell cost, memoised: the callback is the expensive part
  const cellCost = new Int32Array(n).fill(-2);
  const costOf = (i: number): number => {
    let c = cellCost[i]!;
    if (c === -2) { c = cost(i); cellCost[i] = c; }
    return c;
  };
  const heuristic = (x: number, y: number): number => {
    const dx = x > tx ? x - tx : tx - x, dy = y > ty ? y - ty : ty - y;
    const diag = dx < dy ? dx : dy;
    return MIN_CELL_COST * (DIAG_STEP * diag + STRAIGHT_STEP * (dx + dy - 2 * diag));
  };

  // Binary heap over (priority, state) in typed arrays, ties broken by state
  // so the path is a function of the inputs and not of push order.
  let cap = 1 << 14;
  let hp = new Int32Array(cap), hs = new Int32Array(cap);
  let size = 0;
  const push = (p: number, s: number): void => {
    if (size === cap) {
      cap *= 2;
      const np = new Int32Array(cap); np.set(hp); hp = np;
      const ns = new Int32Array(cap); ns.set(hs); hs = ns;
    }
    let i = size++;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      const pp = hp[parent]!, ps = hs[parent]!;
      if (pp < p || (pp === p && ps < s)) break;
      hp[i] = pp; hs[i] = ps;
      i = parent;
    }
    hp[i] = p; hs[i] = s;
  };
  const pop = (): number => {
    const top = hs[0]!;
    size--;
    if (size > 0) {
      const p = hp[size]!, s = hs[size]!;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        if (l >= size) break;
        const r = l + 1;
        let c = l;
        if (r < size && (hp[r]! < hp[l]! || (hp[r] === hp[l] && hs[r]! < hs[l]!))) c = r;
        const cp = hp[c]!, cs = hs[c]!;
        if (p < cp || (p === cp && s < cs)) break;
        hp[i] = cp; hs[i] = cs;
        i = c;
      }
      hp[i] = p; hs[i] = s;
    }
    return top;
  };

  // The start has no heading; seed all eight so the first step is free to go
  // any way. `heading` k is the index into DIAG_DX/DIAG_DY.
  const fx = from % w, fy = (from - fx) / w;
  for (let k = 0; k < 8; k++) { dist[from * 8 + k] = 0; push(heuristic(fx, fy), from * 8 + k); }

  const closed = new Uint8Array(states);
  let found = -1;
  while (size > 0) {
    const s = pop();
    if (closed[s] === 1) continue;
    closed[s] = 1;
    const d = dist[s]!;
    const heading = s % 8, cur = (s - heading) / 8;
    if (cur === to) { found = s; break; }
    const x = cur % w, y = (cur - x) / w;
    for (let k = 0; k < 8; k++) {
      const nx = x + DIAG_DX[k]!, ny = y + DIAG_DY[k]!;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const nb = ny * w + nx;
      const c = costOf(nb);
      if (c < 0) continue;
      const diagonal = (k & 1) === 1;
      if (diagonal) {
        // both flanks blocked: no corner-to-corner squeeze
        const fa = costOf(y * w + nx), fb = costOf(ny * w + x);
        if (fa < 0 && fb < 0) continue;
      }
      let turn = heading - k;
      if (turn < 0) turn = -turn;
      if (turn > 4) turn = 8 - turn;
      // the seeded start states all sit at 0, so from `from` every heading is
      // reachable at no turn cost through the matching seed
      const nd = d + c * (diagonal ? DIAG_STEP : STRAIGHT_STEP)
        + (cur === from ? 0 : turnCost[turn]!);
      const ns = nb * 8 + k;
      if (nd < dist[ns]!) {
        dist[ns] = nd;
        prev[ns] = s;
        push(nd + heuristic(nx, ny), ns);
      }
    }
  }
  if (found < 0) return null;

  const path: number[] = [];
  for (let s = found; s !== -1; s = prev[s]!) {
    const cell = (s - (s % 8)) / 8;
    if (path.length === 0 || path[path.length - 1] !== cell) path.push(cell);
    if (cell === from) break;
  }
  path.reverse();
  return path[0] === from ? path : null;
}

/**
 * Would a step from `a` to `b` draw as a diagonal join?
 *
 * The renderer draws a band to a corner neighbour only when NEITHER cell
 * flanking the corner carries the overlay (`connectionMask` in
 * `@game/render`'s dualgrid): with a flank paved the corner cell is reached
 * through it and the join is an elbow. The generator's own bookkeeping - the
 * network's degree, its cycles - has to see the same graph the player sees.
 */
export function drawnDiagonal(
  g: Grid, on: (i: number) => boolean, a: number, b: number,
): boolean {
  const ax = xOf(g, a), ay = yOf(g, a), bx = xOf(g, b), by = yOf(g, b);
  if (Math.abs(ax - bx) !== 1 || Math.abs(ay - by) !== 1) return false;
  return !on(idx(g, ax, by)) && !on(idx(g, bx, ay));
}

/**
 * Neighbours of `i` that are joined to it on the DRAWN overlay graph: the four
 * orthogonal ones that are on, plus a corner one only when the join would be
 * drawn as a diagonal.
 */
export function drawnNeighbours(
  g: Grid, on: (i: number) => boolean, i: number, out: number[],
): number[] {
  out.length = 0;
  const x = xOf(g, i), y = yOf(g, i);
  for (let k = 0; k < 8; k++) {
    const nx = x + DIAG_DX[k]!, ny = y + DIAG_DY[k]!;
    if (!inside(g, nx, ny)) continue;
    const nb = idx(g, nx, ny);
    if (!on(nb)) continue;
    if ((k & 1) === 1 && !drawnDiagonal(g, on, i, nb)) continue;
    out.push(nb);
  }
  return out;
}
